import { useToast } from '@app/core/notifications';
import { navigateTo } from '@app/core/routing';
import {
  useWebAppCreateMediaExportJobs,
  useWebAppGetMediaExportJobsById,
  webAppGetMediaExportJobsDownloadUrl,
  WebAppMediaExportJobEnqueueDtoTypeEnum,
} from '@mi-front/api-v2';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { DownloadMediaJobNotification } from './DownloadMediaJobNotification';

const POLL_INTERVAL_MS = 300;
const MAX_WAIT_MS = 2000;

/**
 * Returns a user-facing error message: err.message if present and string, otherwise fallback.
 */
function getDownloadErrorMessage(err: unknown, fallback: string): string {
  return typeof (err as { message?: unknown })?.message === 'string'
    ? (err as { message: string }).message
    : fallback;
}

/** Parameters required to start a media download. Not tied to stream selection. */
export interface DownloadMediaParams {
  stationId: string;
  type:
    | typeof WebAppMediaExportJobEnqueueDtoTypeEnum.AUDIO
    | typeof WebAppMediaExportJobEnqueueDtoTypeEnum.VIDEO;
  from: string;
  to: string;
}

export interface UseDownloadMediaOptions {
  /** Called when the preparing state changes (e.g. to sync with a shared store). */
  onPreparingChange?: (isPreparing: boolean) => void;
}

export interface UseDownloadMediaReturn {
  /** Starts the download flow with the given params. No-op if already loading. */
  startDownload: (params: DownloadMediaParams) => void;
  isPreparingDownload: boolean;
}

/**
 * Hook that runs the download media flow: enqueue job, poll up to 3s via React Query;
 * if completed in time do direct download, otherwise show rich notification.
 * Does not depend on stream selection; callers pass params (e.g. via an adapter).
 */
export function useDownloadMedia(options?: UseDownloadMediaOptions): UseDownloadMediaReturn {
  const { onPreparingChange } = options ?? {};
  const { $t } = useIntl();
  const { showPersistentToast, notifyError } = useToast();
  const createMutation = useWebAppCreateMediaExportJobs();
  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  const [jobIdToPoll, setJobIdToPoll] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPreparing = useCallback(() => {
    setIsPreparingDownload(false);
    onPreparingChange?.(false);
    setJobIdToPoll(null);
  }, [onPreparingChange]);

  const jobQuery = useWebAppGetMediaExportJobsById(jobIdToPoll ?? '', {
    query: {
      enabled: !!jobIdToPoll,
      refetchInterval: (query) => {
        const data = query.state.data;

        if (!data || data.isPending) {
          return POLL_INTERVAL_MS;
        }

        return false;
      },
    },
  });

  // When polling detects COMPLETED (below threshold), fetch URL, navigate, then clear loading
  useEffect(() => {
    if (jobIdToPoll == null || jobQuery.data?.status !== 'COMPLETED') {
      return;
    }

    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const jobId = jobIdToPoll;
    setJobIdToPoll(null);

    webAppGetMediaExportJobsDownloadUrl(jobId)
      .then(({ url }) => {
        navigateTo(url);
        clearPreparing();
      })
      .catch((err) => {
        clearPreparing();
        notifyError(
          getDownloadErrorMessage(
            err,
            $t({
              id: 'logger.streamScreen.downloadMedia.downloadError',
              defaultMessage: 'No se pudo obtener el enlace de descarga',
            }),
          ),
        );
      });
  }, [jobIdToPoll, jobQuery.data?.status, $t, clearPreparing, notifyError]);

  const startDownload = useCallback(
    (params: DownloadMediaParams) => {
      setIsPreparingDownload(true);
      onPreparingChange?.(true);

      createMutation
        .mutateAsync({
          data: {
            stationId: params.stationId,
            type: params.type,
            from: params.from,
            to: params.to,
          },
        })
        .then((res) => {
          const jobId = res.jobId;
          setJobIdToPoll(jobId);

          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            clearPreparing();

            showPersistentToast({
              render: (onClose) => (
                <DownloadMediaJobNotification
                  jobId={jobId}
                  stationId={params.stationId}
                  type={params.type}
                  from={params.from}
                  to={params.to}
                  onClose={onClose}
                />
              ),
            });
          }, MAX_WAIT_MS);
        })
        .catch((err) => {
          clearPreparing();
          notifyError(
            getDownloadErrorMessage(
              err,
              $t({
                id: 'logger.streamScreen.downloadMedia.enqueueError',
                defaultMessage: 'No se pudo iniciar la exportación. Intentá de nuevo.',
              }),
            ),
          );
        });
    },
    [$t, createMutation, clearPreparing, showPersistentToast, notifyError, onPreparingChange],
  );

  return { startDownload, isPreparingDownload };
}
